import type { Nullable } from '@src/shared/types/common';

function toggleFiltersPanel(e: kendo.ui.ButtonClickEvent) {
    const localeDict = window.getLocaleDict().button;
    const toggleFiltersBtn = e.sender.element;
    const filtersId = toggleFiltersBtn.data('toggle-filters');
    const filtersElement = filtersId ? $(`#${filtersId}`) : $('.filters');

    if (filtersElement.is(':visible')) {
        localStorage.setItem(`${location.pathname} -showFilters`, 'false');
        toggleFiltersBtn.text(localeDict.showFiltersPanel);
    }
    else {
        localStorage.removeItem(`${location.pathname} -showFilters`);
        toggleFiltersBtn.text(localeDict.hideFiltersPanel);
    }

    filtersElement.toggle();
    $('.k-widget[data-role="pivotconfigurator"]').toggle();
    $('.k-grid, .k-scheduler').each((_, el) => {
        const $container = $(el);
        const grid = $container.data('kendoGrid');
        const treeList = $container.data('kendoTreeList');
        const scheduler = $container.data('kendoScheduler');

        if (grid) {
            grid.resize();
        }
        else if (treeList) {
            treeList.refresh();
        }
        else if (scheduler) {
            window.adjustSchedulerHeight();
        }
    });
}

function collectFilterControlKeys(container: Nullable<Element>): Set<string> {
    const keys = new Set<string>();

    if (!container) {
        return keys;
    }

    container.querySelectorAll<HTMLElement>('[id], [name]')
        .forEach((element) => {
            if (element.id) {
                keys.add(element.id);
            }

            const name = element.getAttribute('name');

            if (name) {
                keys.add(name);
            }
        });

    return keys;
}

function collectPersistentParams(args: {
    queryParams: URLSearchParams;
    serviceKeys: Set<string>;
    controlKeys: Set<string>;
    filterKeys: Set<string>;
}): URLSearchParams {
    const { queryParams, serviceKeys, controlKeys, filterKeys } = args;

    const persistentParams = new URLSearchParams();

    for (const [key, value] of queryParams.entries()) {
        if (serviceKeys.has(key) || controlKeys.has(key) || filterKeys.has(key)) {
            continue;
        }

        persistentParams.set(key, value);
    }

    return persistentParams;
}

/**
 * URL-параметры, которые не являются фильтрами и не должны перезаписываться при сборе фильтров.
 * IsFilter, showKanban — флаги состояния интерфейса (вид списка/канбана, видимость панели фильтров).
 * settings, schedulerSettings — пользовательские настройки колонок и планировщика.
 * Id — идентификатор редактируемой записи: добавляется при открытии попапа редактирования
 *      и должен исчезать из URL после его закрытия, поэтому не сохраняется как persistent-параметр.
 */
const SERVICE_QUERY_PARAMS = new Set([
    'IsFilter',
    'showKanban',
    'settings',
    'schedulerSettings',
    'Id'
]);

function collectFilters() {
    const path = location.pathname.toLowerCase();
    const isCallServicePage = path.includes('callservice/simplecall');
    const isDict = path.includes('dict');
    const queryParams = window.getQueryParams();

    if ((isDict && queryParams['Id'] && !window.isLoaded) || isCallServicePage) {
        return;
    }

    let filterString = '';
    const noValue = 'novalue';
    const filtersContainer = document.querySelector('.filters');
    const controlKeys = collectFilterControlKeys(filtersContainer);

    $('.filters input, .filters select, .filters .k-button-group').each((_index, element) => {
        const input = $(element);
        const role = input.data().role;

        if (input.hasClass('k-textbox')) {
            const control = element as HTMLInputElement;
            filterString += `${input[0].id}=${(!role && control.value) ? control.value : noValue}&`;
        }
        else if (input.hasClass('k-checkbox')) {
            const control = element as HTMLInputElement;
            filterString += `${control.name}=${control.checked ? '1' : '0'}&`;
        }
        else {
            switch (role) {
                case 'numerictextbox':
                case 'maskedtextbox':
                case 'textbox':
                case 'combobox':
                case 'dropdownlist': {
                    const [dataKey] = Object.keys(input.data()).filter((item) => item.startsWith('kendo'));
                    const control = input.data(dataKey);
                    const rawValue = control?.value();
                    const rawText = control?.text?.();
                    const serialized = rawValue == null || rawValue === '' || rawText === '' ? noValue : rawValue;
                    filterString += `${control?.element.attr('id')}=${serialized}&`;
                    break;
                }
                case 'multiselect': {
                    const control = input.data('kendoMultiSelect');

                    if (control) {
                        const value = control.value() as Array<string | number>;
                        const id = control.element.attr('id');
                        const hasValues = value.length > 0;
                        const dataLength = control.dataSource.data().length;
                        const isAll = dataLength === value.length && dataLength !== 1;
                        const isServerFiltering = control.dataSource.options.serverFiltering;
                        const encodedValue = hasValues
                            ? (isAll && !isServerFiltering
                                ? 'CI_All'
                                : value.map((v) => encodeURIComponent(String(v))).join(','))
                            : noValue;
                        filterString += `${id}=${encodedValue}&`;
                    }

                    break;
                }
                case 'datepicker': {
                    const control = input.data('kendoDatePicker');
                    const value = control?.value();
                    filterString += `${control?.element.attr('id')}=${value ? value.toLocaleDateString('RU-ru') : noValue}&`;
                    break;
                }
                case 'timepicker': {
                    const control = input.data('kendoTimePicker');
                    const value = control?.value();
                    filterString += `${control?.element.attr('id')}=${value ? value.toLocaleTimeString('RU-ru') : noValue}&`;
                    break;
                }
                case 'datetimepicker': {
                    const control = input.data('kendoDateTimePicker');
                    const value = control?.value();
                    filterString += `${control?.element.attr('id')}=${value ? value.toLocaleString('RU-ru') : noValue}&`;
                    break;
                }
                case 'buttongroup': {
                    const control = input.data('kendoButtonGroup');
                    filterString += `${control?.element.attr('id')}=${control?.selectedIndices.join()}&`;
                }
            }
        }
    });

    const kanbanView = $('#kanbanView');
    const grid = window.findFirstGrid();
    const scheduler = $('.k-scheduler').data('kendoScheduler');

    if (kanbanView.length) {
        filterString += `showKanban=${kanbanView.is(':visible')}&`;
    }
    if (grid) {
        filterString += `settings=${window.getState(grid)}&`;
    }
    if (scheduler) {
        filterString += `schedulerSettings=${window.getSchedulerState(scheduler)}&`;
    }

    const filterKeys = new Set<string>();
    for (const part of filterString.split('&')) {
        if (!part) {
            continue;
        }

        const eqIdx = part.indexOf('=');
        if (eqIdx > 0) {
            filterKeys.add(part.slice(0, eqIdx));
        }
    }

    const urlQueryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(queryParams)) {
        urlQueryParams.set(key, value);
    }

    const persistentParams = collectPersistentParams({
        queryParams: urlQueryParams,
        serviceKeys: SERVICE_QUERY_PARAMS,
        controlKeys,
        filterKeys
    });
    const persistentQueryString = [...persistentParams.entries()].map(([key, value]) => `${key}=${value}`).join('&');

    filterString = filterString.slice(0, -1);
    let pathName = location.pathname.toLowerCase();

    if (pathName.endsWith('/my')) {
        pathName = pathName.replace('/my', '');
    }
    if (pathName.endsWith('/fromme')) {
        pathName = pathName.replace('/fromme', '');
    }
    if (pathName.endsWith('/todo')) {
        pathName = pathName.replace('/todo', '');
    }
    if (pathName.endsWith('/tocheck')) {
        pathName = pathName.replace('/tocheck', '');
    }

    const compressed = window.compressToBase64(filterString);
    const persistentPart = persistentQueryString ? `&${persistentQueryString}` : '';
    const newUrl = `${location.origin}${pathName}?IsFilter=0&${compressed}${persistentPart}`;

    history.pushState({ path: newUrl }, '', newUrl);
}

function waitForDataSourceAndSetValue(
    ms: kendo.ui.MultiSelect,
    getValue: (data: kendo.data.ObservableArray) => Array<string | number>,
    callback: () => void
): void {
    ms.dataSource.one('change', ({ sender }: kendo.data.DataSourceChangeEvent) => {
        if (sender) {
            const values = getValue(sender.data());
            ms.value(values);
            callback();
        }
    });

    ms.dataSource.read();
}

async function setFilters() {
    const paramDictionary = window.getQueryParams();
    const noValue = 'novalue';

    const { domKeys, nonDomKeys } = Object.keys(paramDictionary).reduce<{ domKeys: string[]; nonDomKeys: string[] }>(
        (acc, key) => {
            document.getElementById(key) ? acc.domKeys.push(key) : acc.nonDomKeys.push(key);
            return acc;
        },
        { domKeys: [], nonDomKeys: [] }
    );

    /**
     * Счётчик родителей для каждого ключа: показывает, сколько у элемента зависимостей сверху.
     */
    const inDegree = new Map<string, number>(domKeys.map((k) => [k, 0]));
    /**
     * Список детей для каждого ключа: хранит все зависимые элементы, которые ссылаются на этот ключ.
     */
    const children = new Map<string, string[]>(domKeys.map((k) => [k, []]));

    for (const key of domKeys) {
        const parent = $(`#${key}`).data('parent');
        const parentCounter = inDegree.get(key) ?? 0;
        const parentChildren = children.get(parent);

        if (parent && parentChildren) {
            inDegree.set(key, parentCounter + 1);
            parentChildren.push(key);
        }
    }

    /**
     * Получаем ключи фильтров, которые независимые, у них нет родителя
     * */
    const queue = domKeys.filter((k) => inDegree.get(k) === 0);
    const sortedKeys: string[] = [];

    while (queue.length) {
        const node = queue.shift();

        if (node) {
            sortedKeys.push(node);

            for (const child of children.get(node) ?? []) {
                const deg = (inDegree.get(child) ?? 1) - 1;
                inDegree.set(child, deg);

                if (deg === 0) {
                    queue.push(child);
                }
            }
        }
    }

    for (const key of domKeys) {
        if (!sortedKeys.includes(key)) {
            sortedKeys.push(key);
        }
    }

    const parentKeys = new Set(
        sortedKeys.filter((k) => (children.get(k)?.length ?? 0) > 0)
    );

    for (const key of sortedKeys) {
        const control = $(`#${key}`);
        const paramValue = paramDictionary[key];
        const role = control.data('role') as string | undefined;
        const hasValue = paramValue !== noValue;
        const isParent = parentKeys.has(key);

        switch (role) {
            case 'multiselect': {
                await new Promise<void>((resolve) => {
                    const ms = control.data('kendoMultiSelect');

                    if (!ms) {
                        resolve();
                        return;
                    }

                    const isServerFiltering = ms.dataSource.options.serverFiltering ?? false;
                    const dataSourceData = ms.dataSource.data();
                    const valueField = ms.options.dataValueField ?? 'Id';
                    const hasParent = Boolean(control.data('parent'));

                    const done = () => {
                        window.refreshMultiSelectMaxTotal(ms);
                        ms._updateTagListHTML?.();
                        !isParent && ms.trigger('change');
                        resolve();
                    };

                    if (paramValue === 'CI_All') {
                        if (!hasParent) {
                            const total = ms.dataSource.total();
                            const isFullyLoaded = dataSourceData.length > 0 && dataSourceData.length >= total;

                            if (isFullyLoaded) {
                                ms.value(dataSourceData.map((a: kendo.data.ObservableObject) => a.get(valueField)));
                                return done();
                            }
                        }
                        waitForDataSourceAndSetValue(
                            ms,
                            (data) => data.map((a: kendo.data.ObservableObject) => a.get(valueField)),
                            done
                        );
                    }
                    else if (hasValue) {
                        const hasLiteralComma = paramValue.includes(',');
                        const hasEncodedComma = paramValue.includes('%2C');
                        const rawIds = (hasEncodedComma && !hasLiteralComma)
                            ? paramValue.split('%2C')
                            : paramValue.split(',').map((v) => v.replace(/%2C/g, ','));
                        const ids = rawIds.every((v) => /^-?\d+$/.test(v.trim()))
                            ? rawIds.map(Number)
                            : rawIds.map((v) => decodeURIComponent(v.trim()));

                        if (isServerFiltering) {
                            /**
                             * Двухпроходное восстановление при серверной фильтрации.
                             * Проход 1: запрашиваем данные по ID из URL, пробуем установить значения.
                             * Проход 2 (вложенный): если сервер вернул не все ID — делаем второй read()
                             * с флагом text-fallback. Вложенность намеренная — каждая подписка
                             * привязана к своему read() и ждёт именно его завершения.
                             */
                            ms._pendingRestoreIds = ids;
                            ms.dataSource.one('change', () => {
                                delete ms._pendingRestoreIds;
                                ms.value(ids);

                                const restored = (ms.dataItems() as kendo.data.ObservableObject[]).length;
                                if (ids.length > 0 && restored < ids.length) {
                                    ms._pendingRestoreIds = ids;
                                    ms.dataSource.one('change', () => {
                                        delete ms._pendingRestoreIds;
                                        ms.value(ids);
                                        done();
                                    });
                                    ms.dataSource.read();
                                    return;
                                }

                                done();
                            });
                            ms.dataSource.read();
                            return;
                        }

                        if (dataSourceData.length && !hasParent) {
                            ms.value(ids);
                            done();
                        }
                        else {
                            waitForDataSourceAndSetValue(ms, () => ids, done);
                        }
                    }
                    else {
                        ms.value(null);
                        done();
                    }
                });
                break;
            }
            case 'combobox': {
                const cb = control.data('kendoComboBox');

                if (cb) {
                    if (hasValue) {
                        cb.value(decodeURI(paramValue));

                        if (cb.dataSource.options.serverFiltering) {
                            cb.dataSource.read();
                        }
                    }
                    else {
                        cb.value('');
                    }
                }
                break;
            }
            case 'dropdownlist': {
                const dd = control.data('kendoDropDownList');
                dd?.value(hasValue ? decodeURI(paramValue) : '');
                break;
            }
            case 'numerictextbox': {
                const ntb = control.data('kendoNumericTextBox');
                ntb?.value(hasValue ? paramValue : '');
                break;
            }
            case 'maskedtextbox': {
                const mtb = control.data('kendoMaskedTextBox');
                mtb?.value(hasValue ? paramValue : '');
                break;
            }
            case 'textbox': {
                const tb = control.data('kendoTextBox');
                tb?.value(hasValue ? decodeURI(paramValue) : '');
                break;
            }
            case 'buttongroup': {
                const bg = control.data('kendoButtonGroup');
                bg?.select(parseInt(paramValue));
                bg?.trigger('select');
                break;
            }
            case 'datepicker':
            case 'timepicker':
            case 'datetimepicker': {
                const [dataKey] = Object.keys(control.data()).filter((item) => item.startsWith('kendo'));
                const picker = control.data(dataKey);
                // #18843
                picker.value(hasValue ? decodeURI(paramValue.replace(/%E2%80%8E/g, '')) : '');
                break;
            }
            default: {
                const chb = control as JQuery<HTMLInputElement>;
                if (chb.hasClass('k-checkbox') && hasValue) {
                    chb.prop('checked', paramValue === '1');
                    break;
                }

                // #18843
                const cleanValue = paramValue.replace(/%E2%80%8E/g, '');
                control.val(cleanValue === noValue ? '' : decodeURI(cleanValue));
            }
        }
    }

    for (const key of nonDomKeys) {
        const paramValue = paramDictionary[key];

        if (key === 'showKanban') {
            if (paramValue === 'true') {
                const viewButton = $('#btToggleGridView').data('kendoButton');
                viewButton?.trigger('click');
            }
        }
    }
}

function applyFiltersFromQueryParams(grid: kendo.ui.Grid) {
    const queryParams = window.getQueryParams();
    const isShowKanban = queryParams.showKanban === 'false';
    const shouldRebind = !location.search || (grid.element.is(':visible') && isShowKanban);

    shouldRebind && setTimeout(window.rebindDataSource, 0, grid);
}

function recollectFiltersOnSourceChange() {
    const grid = window.findFirstGrid();
    const timeoutFunc = () => setTimeout(window.collectFilters, 100);

    if (grid) {
        if (grid instanceof kendo.ui.Grid && grid.options.pageable) {
            grid.one('dataBound', (e: kendo.ui.GridDataBoundEvent) => {
                const pageSizesDdl = $(e.sender.pager.element).find('[data-role=\'dropdownlist\']').data('kendoDropDownList');
                pageSizesDdl?.bind('change', timeoutFunc);
            });
            grid.pager.bind('change', timeoutFunc);
        }

        grid.bind('columnShow', timeoutFunc);
        grid.bind('columnHide', timeoutFunc);
        grid.bind('sort', timeoutFunc);
        grid.bind('group', timeoutFunc);
    }
}

function getFilterString(
    data: Record<string, unknown>,
    dateFormatter = (d: Date) => d.toLocaleDateString('ru-RU'),
    ignore?: string[],
    isFormatSplitArray: boolean = false
) {
    const entries = Object.entries(data);
    const params = new URLSearchParams();
    const ignoreSet = new Set(ignore);

    for (const [key, value] of entries) {
        if (ignoreSet.has(key)) {
            continue;
        }

        if (value == null) {
            params.set(key, 'null');
        }
        else if (Array.isArray(value)) {
            if (isFormatSplitArray) {
                if (value.length > 0) {
                    const items = value.map((v) => v instanceof Date ? dateFormatter(v) : String(v));
                    for (let i = 0; i < items.length; i++) {
                        params.set(`${key}[${i}]`, items[i]);
                    }
                }
            }
            else {
                const items = value.map((v) => v instanceof Date ? dateFormatter(v) : String(v));
                params.set(key, `[${items.join(',')}]`);
            }
        }
        else if (value instanceof Date) {
            params.set(key, dateFormatter(value));
        }
        else if (typeof value === 'string') {
            params.set(key, value);
        }
        else {
            params.set(key, JSON.stringify(value));
        }
    }

    const encodedWithPercent20 = params.toString().replace(/\+/g, '%20');
    return decodeURI(encodedWithPercent20.toString());
}

window.toggleFiltersPanel = toggleFiltersPanel;
window.collectFilters = collectFilters;
window.setFilters = setFilters;
window.applyFiltersFromQueryParams = applyFiltersFromQueryParams;
window.recollectFiltersOnSourceChange = recollectFiltersOnSourceChange;
window.getFilterString = getFilterString;
